import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ClientOnboardingModal from './ClientOnboardingModal';

const ClientOnboardingProcess = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Remove automatic modal opening
  useEffect(() => {
    setIsModalOpen(true);
  }, []);

  // Onboarding steps data
  const onboardingSteps = [
    {
      step: 1,
      title: "Confidential Consultation",
      description: "Initial strategy discussions and requirement analysis",
      details: [
        "Private one-on-one meeting with our senior strategists",
        "Comprehensive assessment of your campaign goals and challenges",
        "Analysis of your current political positioning and messaging",
        "Identification of key opportunities and potential obstacles",
        "Discussion of budget considerations and timeline expectations"
      ],
      icon: "🔒",
      color: "bg-blue-600",
      gradient: "from-blue-500 to-blue-700"
    },
    {
      step: 2,
      title: "Constituency Research",
      description: "Detailed electoral landscape and voter demographic analysis",
      details: [
        "Comprehensive demographic and psychographic voter profiling",
        "Historical voting pattern analysis and trend identification",
        "Opposition research and SWOT analysis",
        "Key stakeholder mapping and influence network analysis",
        "Local media landscape and communication channel assessment"
      ],
      icon: "📊",
      color: "bg-red-600",
      gradient: "from-red-500 to-red-700"
    },
    {
      step: 3,
      title: "Strategy Proposal",
      description: "Comprehensive campaign planning with timeline and budget development",
      details: [
        "Customized campaign strategy document with clear objectives",
        "Detailed implementation timeline with milestone tracking",
        "Comprehensive budget allocation and resource planning",
        "Multi-channel communication and messaging framework",
        "Risk assessment and contingency planning protocols"
      ],
      icon: "📝",
      color: "bg-purple-600",
      gradient: "from-purple-500 to-purple-700"
    },
    {
      step: 4,
      title: "Campaign Execution",
      description: "Full-scale implementation with dedicated team support",
      details: [
        "Dedicated campaign management team assignment",
        "Real-time performance monitoring and analytics dashboard",
        "Continuous strategy optimization and adjustment",
        "24/7 support and crisis management protocols",
        "Regular progress reporting and stakeholder communication"
      ],
      icon: "🚀",
      color: "bg-green-600",
      gradient: "from-green-500 to-green-700"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-gray-900 to-red-900 text-white py-32">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="mb-8"
          >
            <span className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
              <span className="text-xl">📋</span>
              <span className="font-semibold tracking-wide">CLIENT ONBOARDING</span>
            </span>
          </motion.div>

          <motion.h1
            className="hero-text mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Our Seamless Client
            <span className="text-gradient-victory block mt-2">Onboarding Process</span>
          </motion.h1>

          <motion.p
            className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Experience our structured four-step approach designed to ensure a smooth transition from initial consultation to full campaign execution.
            Our process prioritizes confidentiality, strategic precision, and measurable results at every stage.
          </motion.p>

          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <button
              onClick={() => {
                setIsModalOpen(true);
              }}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg transform hover:scale-105"
            >
              Initiate Confidential Consultation
            </button>
          </motion.div>
        </div>
      </section>

      {/* Onboarding Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Streamlined Onboarding
              <span className="text-gradient-political block mt-2">Four-Step Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our systematic approach ensures comprehensive campaign preparation and execution
            </p>
          </motion.div>

          <div className="space-y-16">
            {onboardingSteps.map((step, index) => (
              <motion.div
                key={step.step}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Step Info */}
                <div className={index % 2 === 0 ? '' : 'lg:order-2'}>
                  <div className={`inline-flex items-center space-x-3 ${step.gradient} bg-gradient-to-r text-white rounded-full px-6 py-3 mb-6`}>
                    <span className="font-bold">Step {step.step}</span>
                  </div>

                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>

                  <p className="text-lg text-gray-600 mb-8">
                    {step.description}
                  </p>

                  <div className="space-y-4">
                    {step.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Step Visual */}
                <div className={`flex items-center justify-center p-8 rounded-2xl ${index % 2 === 0 ? 'lg:order-1' : ''}`}>
                  <div className="relative">
                    <div className={`w-64 h-64 ${step.gradient} rounded-2xl flex items-center justify-center shadow-xl`}>
                      <span className="text-7xl">{step.icon}</span>
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-gray-100">
                      <span className="text-3xl font-bold text-gray-900">0{step.step}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Why Our Onboarding
              <span className="text-gradient-political block mt-2">Process Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our structured approach ensures seamless campaign preparation and execution
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="text-5xl mb-6">🔒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Confidentiality</h3>
              <p className="text-gray-600">
                All discussions and documentation are protected with the highest security standards
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="text-5xl mb-6">⏱️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Time Efficiency</h3>
              <p className="text-gray-600">
                Streamlined process reduces onboarding time by 40% compared to industry standards
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="text-5xl mb-6">🎯</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Precision Targeting</h3>
              <p className="text-gray-600">
                Data-driven approach ensures accurate strategy development from day one
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-gray-900 to-red-900 text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Ready to Begin Your
              <span className="text-gradient-victory block mt-2">Campaign Journey?</span>
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join our structured onboarding process and experience the difference of working with India's leading political strategy firm.
              Initiate your confidential consultation today.
            </p>

            <div className="flex justify-center">
              <button
                onClick={() => {
                  setIsModalOpen(true);
                }}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg transform hover:scale-105"
              >
                Initiate Confidential Consultation
              </button>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-orange-400 mb-2">24 Hours</div>
                <div className="text-white/80">Initial Consultation Setup</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white mb-2">100%</div>
                <div className="text-white/80">Confidential Process</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400 mb-2">30 Days</div>
                <div className="text-white/80">Strategy Development</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <ClientOnboardingModal isOpen={isModalOpen} onClose={handleModalClose} />
    </div>
  );
};

export default ClientOnboardingProcess;
